import { selectors } from 'modules/common';
import { domain } from 'modules/data';
import { asMutable } from 'modules/immutable';
import { useSelector } from 'modules/react-redux';
import { useMemo } from 'react';

import { getColumnDefinitionByKind, TemplateReference } from '../../../../config/standard/columnDefinitions';
import { netSuiteDocumentStatusesByIntegrationCode } from '../../../../config/standard/columnDefinitions.constants';
import ColumnKind from '../../../../config/standard/columnKind';
import { FilterType } from '../../../../data/filters';
import { ReferenceListFilter } from '../../../../data/filters/referenceListFilter';
import { excludedIntegrationCodes } from './SectionReportTypeRequest.constants';
import { SectionReportTypeRequestProps } from './SectionReportTypeRequest.types';

export const useStepReportType = (
    reportConfig: SectionReportTypeRequestProps['reportConfig'],
    onReportConfigChange: SectionReportTypeRequestProps['onReportConfigChange']
) => {
    const company = useSelector(selectors.navigation.getActiveCompany);

    const templateColumn = useMemo(
        () => reportConfig.columns.find((column) => column.kind === ColumnKind.TemplateId)!,
        [reportConfig.columns]
    );
    const companyTemplates = useSelector((state) => selectors.template.getTemplatesByCompanyId(state, company.id));

    const templateOptionsUnfiltered = useSelector(
        (state) =>
            (getColumnDefinitionByKind(ColumnKind.TemplateId, company).getOptions?.(
                state,
                templateColumn,
                company.id,
                domain.ReportType.Request
            ) as TemplateReference[]) || []
    );

    const templateOptions = useMemo(
        () =>
            templateOptionsUnfiltered.filter(
                (templateOption) => !excludedIntegrationCodes.includes(templateOption.integrationCode)
            ),
        [templateOptionsUnfiltered]
    );

    const integrationTemplates = useMemo(
        () => templateOptions.filter((template) => template.integrationCode && !template.otherWorkflow),
        [templateOptions]
    );

    const otherIntegrationTemplates = useMemo(
        () =>
            templateOptions.filter((template) => {
                if (!template.integrationCode || !template.otherWorkflow) {
                    return false;
                }

                if (template.integrationCode !== domain.IntegrationCode.XeroAirwallexBatchPayment) {
                    return true;
                }

                const airwallexTemplate = companyTemplates.find(
                    (companyTemplate) =>
                        companyTemplate.integrationCode === domain.IntegrationCode.XeroAirwallexBatchPayment
                );

                if (airwallexTemplate) {
                    const wasActive = Boolean(airwallexTemplate.version);

                    if (airwallexTemplate.enabled || wasActive) {
                        return true;
                    }
                }

                return false;
            }),
        [companyTemplates, templateOptions]
    );

    const standaloneTemplates = useMemo(
        () => templateOptions?.filter((template) => !template.integrationCode),
        [templateOptions]
    );

    const values = (templateColumn?.filter as ReferenceListFilter)?.values.map((value) => value.id);

    const handleTemplateToggle = (value: TemplateReference) => {
        const newReportConfig = asMutable(reportConfig, { deep: true });
        const templateColumn = newReportConfig.columns.find((column) => column.kind === ColumnKind.TemplateId);

        newReportConfig.reportCode = domain.ReportCode.Request;

        const values = (templateColumn?.filter as ReferenceListFilter)?.values || [];

        const existingValue = values.find((v) => v.id === value.id);
        const hadStandaloneWorkflows = (values as TemplateReference[]).some((value) => !value.integrationCode);

        if (!value.otherWorkflow) {
            if (existingValue) {
                values.splice(values.indexOf(existingValue), 1);
            } else {
                values.push(value);
            }

            (templateColumn?.filter as ReferenceListFilter).values = values.filter((value) =>
                templateOptions.some((t) => t.id === value.id && !t.otherWorkflow)
            );
        } else {
            (templateColumn?.filter as ReferenceListFilter).values = existingValue ? [] : [value];
        }

        const hasStandaloneWorkflows = (values as TemplateReference[]).some((value) => !value.integrationCode);

        if (hasStandaloneWorkflows || hadStandaloneWorkflows) {
            onReportConfigChange({
                ...newReportConfig,
                columns: newReportConfig.columns.map((column) => {
                    if (column.kind === ColumnKind.DocumentDate) {
                        return {
                            ...column,
                            visible: hasStandaloneWorkflows,
                        };
                    }

                    return column;
                }),
            });

            return;
        }

        /**
         * Since NetSuiteDocumentStatus options depend on the selected templates,
         * we need to filter NetSuiteDocumentStatus values each time templates are changed
         * to ensure NetSuiteDocumentStatus accepts only relevant values.
         */

        const statuses: Set<string> | undefined = value.integrationCode
            ? netSuiteDocumentStatusesByIntegrationCode[value.integrationCode]
            : undefined;

        if (statuses) {
            onReportConfigChange({
                ...newReportConfig,
                columns: newReportConfig.columns.map((column) => {
                    if (column.kind === ColumnKind.NetSuiteDocumentStatus) {
                        return {
                            ...column,
                            filter: {
                                type: FilterType.ReferenceList,
                                values: (column.filter as ReferenceListFilter).values.filter((filterValue) => {
                                    return !statuses.has(filterValue.id);
                                }),
                            },
                        };
                    }

                    return column;
                }),
            });

            return;
        }

        onReportConfigChange(newReportConfig);
    };

    const integration = company.integration;
    const integrationName = integration
        ? selectors.integration.getIntegrationTypeName(integration.integrationType)
        : null;

    return {
        integration,
        integrationName,
        integrationTemplates,
        otherIntegrationTemplates,
        standaloneTemplates,
        values,
        templateOptions,
        handleTemplateToggle,
    };
};
